import React, { useState } from 'react';
import systemToolsService from '../services/systemToolsService';

/**
 * System Tools page component
 * Provides access to various system maintenance operations
 */
const SystemTools: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('progressSync');

  // Function to handle API calls and update state
  const handleApiCall = async (apiCall: () => Promise<any>, successMessage: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall();
      setResult({
        message: successMessage,
        data: response,
        timestamp: new Date().toISOString()
      });
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      console.error('API call error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">System Tools</h1>
      
      {/* Tabs */}
      <div className="mb-lg" style={{ borderBottom: '1px solid var(--color-divider)' }}>
        <div className="flex">
          <button 
            className={`btn btn-text ${activeTab === 'progressSync' ? 'text-primary' : ''}`}
            onClick={() => setActiveTab('progressSync')}
            style={{ 
              borderBottom: activeTab === 'progressSync' ? '2px solid var(--color-primary)' : 'none',
              borderRadius: 0,
              padding: 'var(--spacing-md)'
            }}
          >
            Progress Data Sync
          </button>
          <button 
            className={`btn btn-text ${activeTab === 'janitor' ? 'text-primary' : ''}`}
            onClick={() => setActiveTab('janitor')}
            style={{ 
              borderBottom: activeTab === 'janitor' ? '2px solid var(--color-primary)' : 'none',
              borderRadius: 0,
              padding: 'var(--spacing-md)'
            }}
          >
            Janitor
          </button>
          <button 
            className={`btn btn-text ${activeTab === 'syncEndpoints' ? 'text-primary' : ''}`}
            onClick={() => setActiveTab('syncEndpoints')}
            style={{ 
              borderBottom: activeTab === 'syncEndpoints' ? '2px solid var(--color-primary)' : 'none',
              borderRadius: 0,
              padding: 'var(--spacing-md)'
            }}
          >
            Sync Endpoints
          </button>
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="card mb-lg">
        {/* Progress Data Sync Tab */}
        {activeTab === 'progressSync' && (
          <div>
            <h2 className="mb-md">Progress Data Synchronization</h2>
            <p className="mb-md">Run progress data synchronization operations.</p>
            
            <div className="flex gap-md mb-lg">
              <button 
                className="btn btn-primary" 
                onClick={() => handleApiCall(
                  systemToolsService.progressSync.syncAll,
                  'Progress sync for all users has been queued'
                )}
                disabled={loading}
              >
                Queue Sync for All Users
              </button>
            </div>
          </div>
        )}
        
        {/* Janitor Tab */}
        {activeTab === 'janitor' && (
          <div>
            <h2 className="mb-md">Janitor Service</h2>
            <p className="mb-md">Run the janitor service to perform system maintenance tasks.</p>
            
            <div className="flex gap-md mb-lg">
              <button 
                className="btn btn-primary" 
                onClick={() => handleApiCall(
                  systemToolsService.janitor.run,
                  'Janitor service has been executed'
                )}
                disabled={loading}
              >
                Run Janitor Service
              </button>
            </div>
          </div>
        )}
        
        {/* Sync Endpoints Tab */}
        {activeTab === 'syncEndpoints' && (
          <div>
            <h2 className="mb-md">Synchronization Endpoints</h2>
            <p className="mb-md">Run various synchronization operations.</p>
            
            <div className="flex gap-md mb-lg">
              <button 
                className="btn btn-primary" 
                onClick={() => handleApiCall(
                  systemToolsService.syncEndpoints.syncCourseAssignments,
                  'Course assignments synchronization has been queued and will be processed asynchronously'
                )}
                disabled={loading}
              >
                Queue Course Assignments Sync
              </button>
              
              <button 
                className="btn btn-primary" 
                onClick={() => handleApiCall(
                  systemToolsService.syncEndpoints.syncUsers,
                  'User synchronization has been initiated'
                )}
                disabled={loading}
              >
                Sync Users
              </button>
              
              <button 
                className="btn btn-primary" 
                onClick={() => handleApiCall(
                  systemToolsService.syncEndpoints.syncCourses,
                  'Course synchronization has been initiated'
                )}
                disabled={loading}
              >
                Sync Courses
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Results Display */}
      {loading && (
        <div className="card mb-lg p-lg flex justify-center items-center">
          <div className="spinner"></div>
          <span className="ml-md">Processing request...</span>
        </div>
      )}
      
      {error && (
        <div className="card mb-lg p-lg bg-error" style={{ color: 'white' }}>
          <h3 className="mb-sm">Error</h3>
          <p>{error}</p>
        </div>
      )}
      
      {result && !loading && !error && (
        <div className="card mb-lg">
          <h3 className="mb-sm">Result</h3>
          <p className="mb-md text-success">{result.message}</p>
          <p className="mb-sm text-secondary">Timestamp: {new Date(result.timestamp).toLocaleString()}</p>
          
          <div className="mt-md">
            <h4 className="mb-sm">Response Data:</h4>
            <pre style={{ 
              backgroundColor: 'var(--color-background-light)', 
              padding: 'var(--spacing-md)',
              borderRadius: 'var(--border-radius-md)',
              overflow: 'auto',
              maxHeight: '300px'
            }}>
              {JSON.stringify(result.data, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemTools;
